/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeButton,
  DimeChip,
  DimeField,
  DimeFieldDescription,
  DimeFieldError,
  DimeFieldGroup,
  DimeFieldLabel,
  DimeInput,
  DimeInputGroup,
  DimeInputGroupAddon,
  DimeInputGroupInput,
  DimeSeparator,
  DimeSuggestInput,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/input')({
  component: InputDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-background p-6">
      <div className="mx-auto w-full max-w-xs">{children}</div>
    </div>
  );
}

function InputDocs() {
  const [username, setUsername] = React.useState('');
  const [reason, setReason] = React.useState('');
  const usernameError = username.length > 0 && username.length < 3;

  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Input</h1>
      <p className="mb-8 text-muted-foreground">
        A text input component for forms and user data entry with built-in styling and accessibility
        features.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <DimeInput placeholder="Email" />
        </Preview>
      </DocsSection>

      <DocsSection title="Touch size">
        <Preview>
          <DimeInput size="touch" placeholder="Qty" aria-label="Touch size" />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">size="touch"</code> is a 44px target for touch work such as
          picking; <code className="font-mono">DimeQuantityInput</code>,{' '}
          <code className="font-mono">DimeSelect</code> and{' '}
          <code className="font-mono">DimeButton</code> take the same size.
        </p>
      </DocsSection>

      <DocsSection title="Trailing adornment">
        <Preview>
          <DimeInput
            placeholder="Search"
            aria-label="Trailing adornment"
            trailing={<Search className="size-4 text-muted-foreground" aria-hidden="true" />}
          />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">trailing</code> puts an icon or status inside the right edge;{' '}
          <code className="font-mono">DimeSelect</code> and{' '}
          <code className="font-mono">DimeTextarea</code> take it too.
        </p>
      </DocsSection>

      <DocsSection title="Field">
        <Preview>
          <DimeField>
            <DimeFieldLabel htmlFor="field-email">Email</DimeFieldLabel>
            <DimeInput id="field-email" type="email" placeholder="m@example.com" />
            <DimeFieldDescription id="field-email-hint">
              We'll never share your email with anyone else.
            </DimeFieldDescription>
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeField</code>,{' '}
          <code className="font-mono">DimeFieldLabel</code>, and{' '}
          <code className="font-mono">DimeFieldDescription</code> to create an input with a label
          and description.
        </p>
      </DocsSection>

      <DocsSection title="Field group">
        <Preview>
          <DimeFieldGroup>
            <DimeField>
              <DimeFieldLabel htmlFor="fg-first">First name</DimeFieldLabel>
              <DimeInput id="fg-first" placeholder="Test" />
            </DimeField>
            <DimeField>
              <DimeFieldLabel htmlFor="fg-last">Last name</DimeFieldLabel>
              <DimeInput id="fg-last" placeholder="User" />
            </DimeField>
            <DimeField>
              <DimeFieldLabel htmlFor="fg-email">Email</DimeFieldLabel>
              <DimeInput id="fg-email" type="email" placeholder="name@example.com" />
            </DimeField>
          </DimeFieldGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeFieldGroup</code> to show multiple{' '}
          <code className="font-mono">DimeField</code> blocks and to build forms.
        </p>
      </DocsSection>

      <DocsSection title="Disabled">
        <Preview>
          <DimeField data-disabled>
            <DimeFieldLabel htmlFor="disabled-input">Email</DimeFieldLabel>
            <DimeInput id="disabled-input" placeholder="locked@example.com" disabled />
            <DimeFieldDescription>
              You cannot change your email address at this time.
            </DimeFieldDescription>
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use the <code className="font-mono">disabled</code> prop on the input. Add{' '}
          <code className="font-mono">data-disabled</code> to{' '}
          <code className="font-mono">DimeField</code> to style the label and description in the
          disabled state.
        </p>
      </DocsSection>

      <DocsSection title="Invalid">
        <Preview>
          <DimeField data-invalid>
            <DimeFieldLabel htmlFor="invalid-input">Username</DimeFieldLabel>
            <DimeInput
              id="invalid-input"
              aria-invalid
              aria-describedby="invalid-input-error"
              placeholder="testuser"
              defaultValue="jo"
            />
            <DimeFieldError id="invalid-input-error">
              Username must be at least 3 characters.
            </DimeFieldError>
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">aria-invalid</code> on the input to mark it invalid. Add{' '}
          <code className="font-mono">data-invalid</code> to{' '}
          <code className="font-mono">DimeField</code> to style the label. Pair with{' '}
          <code className="font-mono">DimeFieldError</code> for the error message.
        </p>
      </DocsSection>

      <DocsSection title="File">
        <Preview>
          <DimeField>
            <DimeFieldLabel htmlFor="file-input">Attachment</DimeFieldLabel>
            <DimeInput id="file-input" type="file" />
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">type="file"</code> to create a file input.
        </p>
      </DocsSection>

      <DocsSection title="Inline">
        <Preview>
          <DimeField orientation="horizontal">
            <DimeFieldLabel htmlFor="inline-search" className="sr-only">
              Search
            </DimeFieldLabel>
            <DimeInput id="inline-search" placeholder="Search…" />
            <DimeButton>Search</DimeButton>
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeField</code> with{' '}
          <code className="font-mono">orientation="horizontal"</code> to place the label and control
          side by side. Pair with <code className="font-mono">DimeButton</code> to build a search
          input with a button.
        </p>
      </DocsSection>

      <DocsSection title="Grid">
        <div className="rounded-lg border border-border bg-background p-6">
          <DimeFieldGroup className="grid grid-cols-2 gap-4">
            <DimeField>
              <DimeFieldLabel htmlFor="grid-first">First name</DimeFieldLabel>
              <DimeInput id="grid-first" placeholder="Test" />
            </DimeField>
            <DimeField>
              <DimeFieldLabel htmlFor="grid-last">Last name</DimeFieldLabel>
              <DimeInput id="grid-last" placeholder="User" />
            </DimeField>
            <DimeField>
              <DimeFieldLabel htmlFor="grid-email">Email</DimeFieldLabel>
              <DimeInput id="grid-email" type="email" placeholder="name@example.com" />
            </DimeField>
            <DimeField>
              <DimeFieldLabel htmlFor="grid-phone">Phone</DimeFieldLabel>
              <DimeInput id="grid-phone" type="tel" placeholder="+1 555-000-0000" />
            </DimeField>
          </DimeFieldGroup>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Use a grid layout on <code className="font-mono">DimeFieldGroup</code> to place multiple
          inputs side by side.
        </p>
      </DocsSection>

      <DocsSection title="Required">
        <Preview>
          <DimeFieldGroup>
            <DimeField>
              <DimeFieldLabel htmlFor="req-name">
                Full name <span className="text-destructive">*</span>
              </DimeFieldLabel>
              <DimeInput id="req-name" placeholder="Test User" required />
            </DimeField>
            <DimeField>
              <DimeFieldLabel htmlFor="req-email">
                Email <span className="text-destructive">*</span>
              </DimeFieldLabel>
              <DimeInput id="req-email" type="email" placeholder="name@example.com" required />
            </DimeField>
          </DimeFieldGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use the <code className="font-mono">required</code> attribute to indicate required inputs.
          Add a visual indicator in the label — a red asterisk is the common convention.
        </p>
      </DocsSection>

      <DocsSection title="Badge">
        <Preview>
          <DimeField>
            <DimeFieldLabel htmlFor="badge-input" className="flex items-center gap-2">
              API key
              <DimeChip variant="success">Recommended</DimeChip>
            </DimeFieldLabel>
            <DimeInput id="badge-input" type="password" placeholder="sk-…" />
            <DimeFieldDescription>Use an API key for the best security.</DimeFieldDescription>
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Place a <code className="font-mono">DimeChip</code> inside{' '}
          <code className="font-mono">DimeFieldLabel</code> to highlight a recommended or required
          field.
        </p>
      </DocsSection>

      <DocsSection title="Validation">
        <Preview>
          <DimeField data-invalid={usernameError || undefined}>
            <DimeFieldLabel htmlFor="validation-input">Username</DimeFieldLabel>
            <DimeFieldDescription id="validation-hint">At least 3 characters.</DimeFieldDescription>
            <DimeInput
              id="validation-input"
              value={username}
              onChange={(ev) => setUsername(ev.target.value)}
              aria-invalid={usernameError || undefined}
              aria-describedby="validation-hint validation-error"
              placeholder="testuser"
            />
            {usernameError && (
              <DimeFieldError id="validation-error">
                Username must be at least 3 characters.
              </DimeFieldError>
            )}
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Controlled validation — type fewer than 3 characters to trigger the error state.
        </p>
      </DocsSection>

      <DocsSection title="Suggestions">
        <Preview>
          <DimeField>
            <DimeFieldLabel htmlFor="suggest-reason">Reason</DimeFieldLabel>
            <DimeSuggestInput
              id="suggest-reason"
              placeholder="Type or pick a reason"
              suggestions={['Damaged in transit', 'Wrong product', 'Quality issue']}
              value={reason}
              onValueChange={setReason}
            />
          </DimeField>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeSuggestInput</code> for free text with suggestions:
          the list narrows as you type, but any typed value stands. For a choice limited to the
          list, use <code className="font-mono">DimeAutocomplete</code>.
        </p>
      </DocsSection>

      <DocsSection title="Input group">
        <Preview>
          <DimeField>
            <DimeFieldLabel htmlFor="ig-search">Search</DimeFieldLabel>
            <DimeInputGroup>
              <DimeInputGroupAddon>
                <Search />
              </DimeInputGroupAddon>
              <DimeInputGroupInput id="ig-search" placeholder="Search parts…" />
            </DimeInputGroup>
            <DimeFieldDescription>
              For icons, text, or button addons see{' '}
              <a href="/design-system/input-group" className="underline">
                Input Group
              </a>
              .
            </DimeFieldDescription>
          </DimeField>
        </Preview>
      </DocsSection>
    </div>
  );
}
